import { useQuery } from "@tanstack/react-query"
import { type Asset, trpcClient } from "@tokenterminal/core/utils/trpc"

const TOKENIZED_ASSETS_MARKET_SECTORS = [
  "stablecoins",
  "tokenized-stocks",
  "tokenized-funds",
  "tokenized-commodities",
]

const METRIC_ID = "asset_market_cap_circulating"
const METRIC_DISPLAY_NAME = "Circulating asset market cap"

interface LeaderboardItem {
  rank: number
  name: string
  logo: string
  assetId: string
  marketCap: number
  change: number
}

export function useLeaderboardData() {
  const { data: assetsData, isLoading: isAssetsLoading } = useQuery({
    queryKey: ["assets-list", TOKENIZED_ASSETS_MARKET_SECTORS],
    queryFn: async () => {
      const result = await trpcClient.asset.getAssets.query({
        market_sectors: TOKENIZED_ASSETS_MARKET_SECTORS,
      })
      return result.data
    },
    staleTime: 1000 * 60 * 10,
  })

  const { data: metricsData, isLoading: isMetricsLoading } = useQuery({
    queryKey: [
      "asset-metric-breakdown",
      METRIC_ID,
      "30d",
      TOKENIZED_ASSETS_MARKET_SECTORS,
    ],
    queryFn: async () => {
      const result = await trpcClient.asset.getAssetMetricsBreakdown.mutate({
        metric_ids: [METRIC_ID],
        interval: "30d",
        groupBy: "assets",
        market_sector_ids: TOKENIZED_ASSETS_MARKET_SECTORS,
        bridged: false,
      })
      return result.data
    },
    staleTime: 1000 * 60 * 5,
  })

  const isLoading = isAssetsLoading || isMetricsLoading

  const assetsMap = new Map<string, Asset>()
  if (assetsData) {
    for (const asset of assetsData) {
      assetsMap.set(asset.asset_id, asset)
    }
  }

  const leaderboardData: LeaderboardItem[] = []

  if (metricsData && assetsData) {
    const sortedMetrics = [...metricsData]
      .filter((item) => {
        const metric = item.metrics[METRIC_ID]
        return item.asset_id && metric?.latest != null && metric.latest > 0
      })
      .sort((a, b) => {
        const aValue = a.metrics[METRIC_ID]?.latest ?? 0
        const bValue = b.metrics[METRIC_ID]?.latest ?? 0
        return bValue - aValue
      })
      .slice(0, 10)

    for (let i = 0; i < sortedMetrics.length; i++) {
      const metricItem = sortedMetrics[i]
      const assetId = metricItem.asset_id
      if (!assetId) continue
      const asset = assetsMap.get(assetId)

      if (asset) {
        leaderboardData.push({
          rank: i + 1,
          name: asset.symbol || asset.name || assetId,
          logo: asset.logo || "",
          assetId: assetId,
          marketCap: metricItem.metrics[METRIC_ID]?.latest ?? 0,
          change: (metricItem.metrics[METRIC_ID]?.change ?? 0) * 100,
        })
      }
    }
  }

  return {
    leaderboardData,
    isLoading,
    metricDisplayName: METRIC_DISPLAY_NAME,
  }
}
